import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { PageHeader, useMeta } from "../components/Shared";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { ROLES } from "../lib/format";
import { ListManager } from "../components/ListManager";
import { AnnouncementsManager } from "../components/AnnouncementsManager";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";

const LOGIN_PRESETS = [
  { id: "solar", label: "Solar farm", url: "https://static.prod-images.emergentagent.com/jobs/ec99e417-b4fc-4ffa-8f63-7f2d00809cac/images/cde57a8a8f3f8d0de641dec17bd1e8d8653be8afedb664ed70f57c2d63fb6dcf.jpeg" },
  { id: "grid", label: "Power grid", url: "https://static.prod-images.emergentagent.com/jobs/ec99e417-b4fc-4ffa-8f63-7f2d00809cac/images/2ca4e9a5df597dea34fcc221e3e13b1d0e295e782a4576de2981d02a30004eaa.jpeg" },
  { id: "hospital", label: "Medical equipment", url: "https://static.prod-images.emergentagent.com/jobs/ec99e417-b4fc-4ffa-8f63-7f2d00809cac/images/0683f66e03272737e6773e191ad1e899be44d4e4102f0c6a1192a83f3541a8cd.jpeg" },
  { id: "abstract", label: "Abstract tech", url: "https://static.prod-images.emergentagent.com/jobs/ec99e417-b4fc-4ffa-8f63-7f2d00809cac/images/a4530a9294b12388298546136c8a582ba866788def18c1d9936be1eaccd8d408.jpeg" },
];
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Bell, Send, Save, Palette, LayoutTemplate, Trash2, Upload, Plus, X, Copy, Share2, KeyRound, ShieldCheck, Unlock, Eye, Database, Download, RefreshCw, RotateCcw, Sun, Moon, Monitor, Building2 } from "lucide-react";
import PhoneField from "../components/PhoneField";
import { isValidEmail } from "../lib/validate";
import { toast } from "sonner";

const PALETTES = [
  { name: "Navy", color: "#0B132B" }, { name: "Royal Blue", color: "#1E3A8A" },
  { name: "Emerald", color: "#047857" }, { name: "Amber", color: "#B45309" },
  { name: "Crimson", color: "#9F1239" }, { name: "Violet", color: "#5B21B6" },
  { name: "Teal", color: "#0F766E" }, { name: "Slate", color: "#334155" },
];

const cropSquare = (file) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const size = Math.min(img.width, img.height);
    const canvas = document.createElement("canvas");
    canvas.width = 256; canvas.height = 256;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, (img.width - size) / 2, (img.height - size) / 2, size, size, 0, 0, 256, 256);
    let colors = [];
    try {
      const d = ctx.getImageData(0, 0, 256, 256).data;
      const buckets = {};
      for (let i = 0; i < d.length; i += 16) {
        if (d[i + 3] < 128) continue;
        const r = d[i], g = d[i + 1], b = d[i + 2];
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        if (lum > 235 || lum < 18) continue;
        const key = `${r >> 5},${g >> 5},${b >> 5}`;
        const bk = buckets[key] || (buckets[key] = { r: 0, g: 0, b: 0, n: 0 });
        bk.r += r; bk.g += g; bk.b += b; bk.n++;
      }
      const adj = (r, g, b) => {
        const lum = 0.299 * r + 0.587 * g + 0.114 * b;
        const f = lum > 175 ? 0.55 : (lum < 45 ? 1.7 : 1);
        const cl = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
        return "#" + [cl(r), cl(g), cl(b)].map((v) => v.toString(16).padStart(2, "0")).join("");
      };
      colors = [...new Set(Object.values(buckets).sort((a, b) => b.n - a.n).slice(0, 3)
        .map((bk) => adj(bk.r / bk.n, bk.g / bk.n, bk.b / bk.n)))];
    } catch (e) { colors = []; }
    canvas.toBlob((blob) => resolve({ file: new File([blob], "logo.png", { type: "image/png" }), colors }), "image/png");
  };
  img.onerror = () => resolve({ file, colors: [] });
  img.src = URL.createObjectURL(file);
});

const ALERT_TYPES = [
  { key: "deadline", label: "Project deadlines (overdue & due soon)" },
  { key: "balance", label: "Cash shortfalls (negative balances)" },
  { key: "budget", label: "Over-budget projects" },
  { key: "approval", label: "Pending user approvals" },
  { key: "payment", label: "Payments awaiting approval" },
  { key: "check", label: "Overdue payment checks" },
];

export default function Settings() {
  const meta = useMeta();
  const { isRole, user, setMyPhone } = useAuth();
  const { mode, setMode } = useTheme();
  const [s, setS] = useState(null);
  const [company, setCompany] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [newTpl, setNewTpl] = useState({ name: "", stages: [""], category: "" });
  const [logoColors, setLogoColors] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [colorDrag, setColorDrag] = useState(null);
  const [managers, setManagers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [twoFaPhone, setTwoFaPhone] = useState(user?.phone || "");
  const saveTwoFaPhone = async () => {
    const res = await setMyPhone((twoFaPhone || "").trim());
    if (res.ok) toast.success("Two-factor phone updated."); else toast.error(res.error || "Could not update phone.");
  };

  const isCEO = isRole(ROLES.CEO);
  const [mySig, setMySig] = useState(null);
  useEffect(() => { if (!isCEO) api.get("/settings/my-signature").then(({ data }) => setMySig(data)).catch(() => {}); }, [isCEO]);
  const uploadMySig = async (file) => {
    if (!file || !mySig?.role) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/settings/signature/${mySig.role}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setMySig({ ...mySig, url: data.url }); toast.success("Signature uploaded"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeMySig = async () => {
    try { await api.delete(`/settings/signature/${mySig.role}`); setMySig({ ...mySig, url: null }); toast.success("Signature removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { if (isCEO) api.get("/settings").then(({ data }) => setS(data)); }, [isCEO]);
  useEffect(() => {
    if (isRole(ROLES.CEO)) api.get("/users").then(({ data }) => setManagers((data || []).filter((u) => u.role !== ROLES.CEO && String(u.status).toLowerCase() === "active"))).catch(() => {});
  }, [isRole]);
  useEffect(() => { if (isCEO) api.get("/milestone-templates").then(({ data }) => setTemplates(data)).catch(() => {}); }, [isCEO]);
  useEffect(() => {
    if (!isCEO) return;
    const cid = localStorage.getItem("stems_company");
    api.get("/companies").then(({ data }) => {
      setAllCompanies(data);
      const c = data.find((x) => x.id === cid) || data[0];
      if (c) setCompany({ ...c, logo_url: c.logo_url || "", accent_color: c.accent_color || "#0B132B", logo_position: c.logo_position || "left", tagline: c.tagline || "", login_heading: c.login_heading || "", login_tagline: c.login_tagline || "", login_bg_url: c.login_bg_url || "", login_overlay: c.login_overlay ?? 75, login_footer: c.login_footer ?? "", login_logo_size: c.login_logo_size ?? 80, login_logo_position: c.login_logo_position || "center" });
    });
  }, [isCEO]);

  if (!isCEO) return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Manage your contract signature" />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 max-w-md">
        <Label className="text-sm">My signature</Label>
        <div className="text-xs text-slate-400 mb-3">Upload your signature to stamp on the sub-contracts you prepare. A PNG with a transparent background works best.</div>
        {mySig?.url ? (
          <img src={mySig.url} alt="My signature" className="h-16 object-contain mb-2" data-testid="my-sig-preview" />
        ) : (
          <div className="h-16 flex items-center text-xs text-slate-400 mb-2">No signature uploaded</div>
        )}
        <div className="flex items-center gap-2">
          <input type="file" accept="image/png,image/jpeg,image/webp" data-testid="my-sig-upload" onChange={(e) => uploadMySig(e.target.files?.[0])} className="text-xs" />
          {mySig?.url && <Button size="sm" variant="ghost" className="text-red-600 h-7" data-testid="my-sig-remove" onClick={removeMySig}>Remove</Button>}
        </div>
      </div>
    </div>
  );

  if (!s && isRole(ROLES.CEO)) return <div className="text-slate-400">Loading settings…</div>;

  const toggleType = (k) => {
    const has = s.alert_types.includes(k);
    setS({ ...s, alert_types: has ? s.alert_types.filter((x) => x !== k) : [...s.alert_types, k] });
  };
  const toggleRecipient = (id) => {
    const cur = s.digest_recipient_ids || [];
    setS({ ...s, digest_recipient_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const saveOrg = async () => {
    if (s.org_email && !isValidEmail(s.org_email)) { toast.error("Enter a valid official email address."); return; }
    try {
      const { data } = await api.put("/settings/organization", { org_reg_no: s.org_reg_no || "", org_email: s.org_email || "", org_phone: s.org_phone || "", org_address: s.org_address || "", org_website: s.org_website || "", org_taxpayer: s.org_taxpayer || "", org_ceo_name: s.org_ceo_name || "", org_fm_name: s.org_fm_name || "" });
      const lab = s.est_labour_percent === "" || s.est_labour_percent == null ? 30 : Number(s.est_labour_percent);
      const { data: d2 } = await api.put("/settings/estimates", { labour_percent: lab });
      setS({ ...data, est_labour_percent: d2.est_labour_percent }); toast.success("Organization details saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const save = async () => {
    try {
      const { data } = await api.put("/settings/digest", {
        digest_enabled: s.digest_enabled, digest_hour: Number(s.digest_hour),
        alert_types: s.alert_types, budget_email_enabled: s.budget_email_enabled,
        payment_email_enabled: s.payment_email_enabled, payment_email_cc: s.payment_email_cc || "",
        sms_alerts_enabled: s.sms_alerts_enabled, digest_recipient_ids: s.digest_recipient_ids || [],
        allowance_reminder_lead_days: Number(s.allowance_reminder_lead_days) || 0,
        allowance_reminder_email: !!s.allowance_reminder_email,
        intern_reminder_days: Number(s.intern_reminder_days) || 0,
      });
      setS(data);
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const uploadSig = async (role, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/settings/signature/${role}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS({ ...s, [`${role}_signature_url`]: data.url });
      toast.success("Signature uploaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeSig = async (role) => {
    try { await api.delete(`/settings/signature/${role}`); setS({ ...s, [`${role}_signature_url`]: null }); toast.success("Signature removed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendNow = async () => {
    try { await api.post("/settings/digest/test"); toast.success("Digest is being sent to your inbox"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const openPreview = async () => {
    try { const { data } = await api.get("/settings/digest/preview"); setPreview(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendPreviewToMe = async () => {
    try { const { data } = await api.post("/settings/digest/preview/send"); toast.success(`Preview sent to ${data.to}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send preview"); }
  };
  const saveBrand = async () => {
    try {
      const { data } = await api.put(`/companies/${company.id}`, {
        logo_url: company.logo_url, accent_color: company.accent_color,
        logo_position: company.logo_position || "left", tagline: company.tagline || "",
        login_heading: company.login_heading || "", login_tagline: company.login_tagline || "",
        login_bg_url: company.login_bg_url || "", login_overlay: company.login_overlay ?? 75,
        login_footer: company.login_footer ?? "", login_logo_size: company.login_logo_size ?? 80,
        login_logo_position: company.login_logo_position || "center",
      });
      setCompany({ ...company, ...data });
      toast.success("Branding saved — client portals updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resetLoginPanel = async () => {
    const cleared = { login_heading: "", login_tagline: "", login_bg_url: "", login_overlay: 75, login_footer: "", login_logo_size: 80, login_logo_position: "center" };
    setCompany((c) => ({ ...c, ...cleared }));
    try {
      await api.put(`/companies/${company.id}`, cleared);
      toast.success("Login panel reset to defaults");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const uploadLoginBg = async (file) => {
    if (!file) return;
    const form = new FormData(); form.append("file", file);
    try {
      const { data } = await api.post(`/companies/${company.id}/login-bg`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany((c) => ({ ...c, login_bg_url: data.login_bg_url }));
      toast.success("Background uploaded — click Save Branding");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const uploadLoginPreset = async (file) => {
    if (!file) return;
    const form = new FormData(); form.append("file", file);
    try {
      const { data } = await api.post(`/companies/${company.id}/login-preset`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany((c) => ({ ...c, login_bg_presets: [...(c.login_bg_presets || []), data] }));
      toast.success("Preset added to your gallery");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteLoginPreset = async (id) => {
    try {
      await api.delete(`/companies/${company.id}/login-preset/${id}`);
      setCompany((c) => ({ ...c, login_bg_presets: (c.login_bg_presets || []).filter((p) => p.id !== id) }));
      toast.success("Preset removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    const { file: cropped, colors } = await cropSquare(file);
    const form = new FormData(); form.append("file", cropped);
    try {
      const { data } = await api.post(`/companies/${company.id}/logo`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setCompany((c) => ({ ...c, logo_url: data.logo_url, accent_color: colors[0] || c.accent_color }));
      setLogoColors(colors);
      toast.success(colors.length ? "Logo squared & accent matched — click Save Branding" : "Logo uploaded — click Save Branding");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const duplicateTpl = async (t) => {
    try {
      await api.post("/milestone-templates", { name: `${t.name} (copy)`, stages: t.stages, category: t.category || "" });
      const { data } = await api.get("/milestone-templates"); setTemplates(data);
      toast.success("Template duplicated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const copyTplTo = async (t, targetId, targetName) => {
    try { await api.post(`/milestone-templates/${t.id}/copy`, { target_company_id: targetId }); toast.success(`Copied to ${targetName}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const reorderColor = (i) => {
    if (colorDrag === null || colorDrag === i) { setColorDrag(null); return; }
    setLogoColors((cs) => {
      const a = [...cs]; const [x] = a.splice(colorDrag, 1); a.splice(i, 0, x);
      setCompany((cp) => ({ ...cp, accent_color: a[0] }));
      return a;
    });
    setColorDrag(null);
  };
  const createTpl = async () => {
    const stages = newTpl.stages.map((s) => s.trim()).filter(Boolean);
    if (!newTpl.name.trim() || stages.length === 0) { toast.error("Add a name and at least one stage"); return; }
    try {
      await api.post("/milestone-templates", { name: newTpl.name.trim(), stages, category: newTpl.category || "" });
      const { data } = await api.get("/milestone-templates"); setTemplates(data);
      setTplOpen(false); setNewTpl({ name: "", stages: [""], category: "" });
      toast.success("Template created");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const renameTpl = (id, name) => setTemplates((ts) => ts.map((x) => (x.id === id ? { ...x, name } : x)));
  const patchTpl = (id, patch) => setTemplates((ts) => ts.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const saveTpl = async (t) => {
    try { await api.put(`/milestone-templates/${t.id}`, { name: t.name, category: t.category || "" }); toast.success("Template saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteTpl = async (t) => {
    try { await api.delete(`/milestone-templates/${t.id}`); setTemplates((ts) => ts.filter((x) => x.id !== t.id)); toast.success("Template deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl">
      <PageHeader title="Settings" subtitle="Daily digest and alert preferences" />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6" data-testid="appearance-card">
        <div className="flex items-center gap-2 mb-1">
          <Palette className="h-5 w-5 text-slate-500" />
          <div className="font-heading font-semibold text-slate-800">Appearance</div>
        </div>
        <p className="text-sm text-slate-500 mb-4">Choose how STEMS looks on this device.</p>
        <div className="grid grid-cols-3 gap-3 max-w-md">
          {[
            { key: "light", label: "Light", icon: Sun },
            { key: "dark", label: "Dark", icon: Moon },
            { key: "system", label: "System", icon: Monitor },
          ].map((o) => (
            <button
              key={o.key}
              type="button"
              data-testid={`theme-mode-${o.key}`}
              onClick={() => setMode(o.key)}
              className={`flex flex-col items-center gap-2 rounded-xl border p-4 transition-colors ${mode === o.key ? "border-[#1E3A8A] bg-blue-50 text-[#1E3A8A]" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
            >
              <o.icon className="h-5 w-5" />
              <span className="text-sm font-medium">{o.label}</span>
            </button>
          ))}
        </div>
      </div>


      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mt-6" data-testid="twofa-phone-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="h-5 w-5 text-slate-500" />
          <div className="font-heading font-semibold text-slate-800">Two-factor authentication</div>
        </div>
        <p className="text-sm text-slate-500 mb-4">Your login code is sent by SMS to this mobile number. Leave it blank to receive the code by email instead.</p>
        <div className="flex flex-wrap items-end gap-3 max-w-md">
          <div className="space-y-1.5 flex-1 min-w-[220px]">
            <Label className="text-sm">Mobile number (international format)</Label>
            <Input data-testid="twofa-phone-input" value={twoFaPhone} onChange={(e) => setTwoFaPhone(e.target.value)} placeholder="+237677123456" />
          </div>
          <Button data-testid="twofa-phone-save" onClick={saveTwoFaPhone} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Save className="h-4 w-4 mr-2" /> Save</Button>
        </div>
      </div>

      {isCEO && s && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mt-6" data-testid="org-details-card">
        <div className="flex items-center gap-2 mb-1">
          <Building2 className="h-5 w-5 text-slate-500" />
          <div className="font-heading font-semibold text-slate-800">Organization details</div>
        </div>
        <p className="text-sm text-slate-500 mb-4">These appear at the top of every official document (PDF letterheads), the student report form and all system emails. Updating them here updates every document at once.</p>
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-sm">Registration number</Label>
            <Input data-testid="org-reg-no-input" value={s.org_reg_no || ""} onChange={(e) => setS({ ...s, org_reg_no: e.target.value })} placeholder="RC.B'DA.2026A.227 of 29 April 2026" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-sm">Taxpayer number</Label>
            <Input data-testid="org-taxpayer-input" value={s.org_taxpayer || ""} onChange={(e) => setS({ ...s, org_taxpayer: e.target.value })} placeholder="P000000000000X" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-sm">Physical office address</Label>
            <Input data-testid="org-address-input" value={s.org_address || ""} onChange={(e) => setS({ ...s, org_address: e.target.value })} placeholder="P.O. Box 1234, Bamenda, North-West Region, Cameroon" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-sm">CEO name on documents</Label>
            <Input data-testid="org-ceo-name-input" value={s.org_ceo_name || ""} onChange={(e) => setS({ ...s, org_ceo_name: e.target.value })} placeholder="e.g. Silas Formunyuy — printed under the CEO signature" />
            <p className="text-xs text-slate-400">Printed as the CEO's name on every exported PDF signature (receipts, estimates, quotes, sub-contracts, solar, payslips). Leave blank to use the Administration record or login account name.</p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-sm">Finance Manager name on documents</Label>
            <Input data-testid="org-fm-name-input" value={s.org_fm_name || ""} onChange={(e) => setS({ ...s, org_fm_name: e.target.value })} placeholder="e.g. Jane Ndum — printed under the Finance Manager signature" />
            <p className="text-xs text-slate-400">Printed as the Finance Manager's name on every exported PDF signature. Leave blank to use the login account name.</p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-sm">Default labour % on new estimates</Label>
            <Input type="number" min="0" step="any" className="max-w-[160px]" data-testid="org-labour-percent-input" value={s.est_labour_percent ?? 30} onChange={(e) => setS({ ...s, est_labour_percent: e.target.value })} />
            <p className="text-xs text-slate-400">Starting labour percentage (of the materials subtotal) for every new estimate. It can still be changed on each estimate; existing estimates are not affected.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Official email</Label>
            <Input type="email" data-testid="org-email-input" value={s.org_email || ""} onChange={(e) => setS({ ...s, org_email: e.target.value })} placeholder="info@silastech.cm" className={s.org_email && !isValidEmail(s.org_email) ? "border-red-400 focus-visible:ring-red-400" : ""} />
            {s.org_email && !isValidEmail(s.org_email) && <p className="text-xs text-red-500" data-testid="org-email-error">Enter a valid email address.</p>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Official phone</Label>
            <PhoneField testId="org-phone-input" value={s.org_phone || ""} onChange={(v) => setS({ ...s, org_phone: v })} placeholder="Official phone" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-sm">Website</Label>
            <Input data-testid="org-website-input" value={s.org_website || ""} onChange={(e) => setS({ ...s, org_website: e.target.value })} placeholder="www.silastech.cm" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button data-testid="org-save-button" onClick={saveOrg} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Save className="h-4 w-4 mr-2" /> Save organization details</Button>
          <Button variant="outline" data-testid="download-letterhead-docx" onClick={() => downloadExport("/export/letterhead-template.docx", "letterhead-template.docx")}><Download className="h-4 w-4 mr-2" /> Download letterhead (Word)</Button>
        </div>
      </div>
      )}

      {isRole(ROLES.CEO) && s && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-slate-500" />
            <div>
              <div className="font-heading font-semibold text-slate-800">Daily email digest</div>
              <div className="text-sm text-slate-500">Sent to all CEO accounts.</div>
            </div>
          </div>
          <Switch checked={s.digest_enabled} onCheckedChange={(v) => setS({ ...s, digest_enabled: v })} data-testid="digest-enabled-switch" />
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-5">
          <div>
            <Label className="text-sm">Allowance reminder lead time</Label>
            <div className="text-xs text-slate-400">Days before an allowance is due that the Finance Manager is reminded.</div>
          </div>
          <div className="flex items-center gap-2">
            <Input type="number" min={0} max={30} value={s.allowance_reminder_lead_days ?? 2}
              onChange={(e) => setS({ ...s, allowance_reminder_lead_days: e.target.value })}
              className="w-20 text-right" data-testid="allowance-lead-input" />
            <span className="text-sm text-slate-500">day(s)</span>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-5">
          <div>
            <Label className="text-sm">Student deadline reminder</Label>
            <div className="text-xs text-slate-400">Days before a student's activity window closes that they are emailed a reminder.</div>
          </div>
          <div className="flex items-center gap-2">
            <Input type="number" min={0} max={30} value={s.intern_reminder_days ?? 1}
              onChange={(e) => setS({ ...s, intern_reminder_days: e.target.value })}
              className="w-20 text-right" data-testid="intern-reminder-days-input" />
            <span className="text-sm text-slate-500">day(s)</span>
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-5">
          <Label className="text-sm">Send time (UTC)</Label>
          <Select value={String(s.digest_hour)} onValueChange={(v) => setS({ ...s, digest_hour: Number(v) })}>
            <SelectTrigger className="w-32" data-testid="digest-hour-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-64">
              {Array.from({ length: 24 }).map((_, h) => (
                <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border-t border-slate-100 pt-5">
          <Label className="text-sm">Include these alerts</Label>
          <div className="mt-3 space-y-2">
            {ALERT_TYPES.map((t) => (
              <label key={t.key} className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" data-testid={`alert-${t.key}`} checked={s.alert_types.includes(t.key)}
                  onChange={() => toggleType(t.key)} className="h-4 w-4 rounded border-slate-300 text-blue-700" />
                {t.label}
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-5">
          <div>
            <div className="font-medium text-slate-800 text-sm">Instant budget alerts</div>
            <div className="text-sm text-slate-500">Email the moment a project exceeds its contract value.</div>
          </div>
          <Switch checked={s.budget_email_enabled} onCheckedChange={(v) => setS({ ...s, budget_email_enabled: v })} data-testid="budget-email-switch" />
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-5">
          <div>
            <div className="font-medium text-slate-800 text-sm">Payment alerts</div>
            <div className="text-sm text-slate-500">Email you the moment the Financial Manager records a payment.</div>
          </div>
          <Switch checked={s.payment_email_enabled !== false} onCheckedChange={(v) => setS({ ...s, payment_email_enabled: v })} data-testid="payment-email-switch" />
        </div>

        <div className="border-t border-slate-100 pt-5">
          <Label className="text-sm">Also CC an address on payment alerts <span className="text-xs font-normal text-slate-400">· optional</span></Label>
          <Input type="email" value={s.payment_email_cc || ""} onChange={(e) => setS({ ...s, payment_email_cc: e.target.value })} placeholder="accountant@company.com" data-testid="payment-email-cc" className="mt-1.5" />
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-5">
          <div>
            <div className="font-medium text-slate-800 text-sm">Beneficiary SMS alerts</div>
            <div className="text-sm text-slate-500">Text the beneficiary a confirmation when a payment check is issued (requires a phone number on the check).</div>
          </div>
          <Switch checked={!!s.sms_alerts_enabled} onCheckedChange={(v) => setS({ ...s, sms_alerts_enabled: v })} data-testid="sms-alerts-switch" />
        </div>

        <div className="border-t border-slate-100 pt-5">
          <Label className="text-sm">Also send digest to</Label>
          <div className="text-xs text-slate-400 mb-3">The CEO always receives it. Tick which managers should also get the daily digest.</div>
          {managers.length === 0 ? (
            <div className="text-sm text-slate-400" data-testid="digest-no-managers">No other managers yet.</div>
          ) : (
            <div className="space-y-2">
              {managers.map((m) => (
                <label key={m.id} className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer">
                  <input type="checkbox" data-testid={`digest-recipient-${m.id}`}
                    checked={(s.digest_recipient_ids || []).includes(m.id)}
                    onChange={() => toggleRecipient(m.id)}
                    className="h-4 w-4 rounded border-slate-300 text-blue-700" />
                  <span>{m.name} <span className="text-slate-400">· {m.role} · {m.email}</span></span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-5">
          <div>
            <div className="font-medium text-slate-800 text-sm">Email the Finance Manager allowance reminders</div>
            <div className="text-sm text-slate-500">Also email the FM each day when allowances are due (not just the in-app bell).</div>
          </div>
          <Switch checked={!!s.allowance_reminder_email} onCheckedChange={(v) => setS({ ...s, allowance_reminder_email: v })} data-testid="allowance-email-switch" />
        </div>

        <div className="border-t border-slate-100 pt-5">
          <Label className="text-sm">Contract & payslip signatures</Label>
          <div className="text-xs text-slate-400 mb-3">Upload signatures to stamp on generated payslips and sub-contracts. The Assistant CEO and General Manager can also upload their own from their Settings. A PNG with a transparent background works best.</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[["ceo", "CEO"], ["fm", "Finance Manager"], ["aceo", "Assistant CEO"], ["gm", "General Manager"]].map(([role, label]) => (
              <div key={role} className="rounded-lg border border-slate-200 p-3">
                <div className="text-sm font-medium text-slate-700 mb-2">{label}</div>
                {s[`${role}_signature_url`] ? (
                  <img src={s[`${role}_signature_url`]} alt={`${label} signature`} className="h-14 object-contain mb-2" data-testid={`sig-preview-${role}`} />
                ) : (
                  <div className="h-14 flex items-center text-xs text-slate-400 mb-2">No signature uploaded</div>
                )}
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/png,image/jpeg,image/webp" data-testid={`sig-upload-${role}`}
                    onChange={(e) => uploadSig(role, e.target.files?.[0])} className="text-xs" />
                  {s[`${role}_signature_url`] && <Button size="sm" variant="ghost" className="text-red-600 h-7" data-testid={`sig-remove-${role}`} onClick={() => removeSig(role)}>Remove</Button>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-5">
          <Button onClick={save} data-testid="save-settings-button" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Save className="h-4 w-4 mr-2" /> Save Changes</Button>
          <Button variant="outline" onClick={sendNow} data-testid="send-test-digest-button"><Send className="h-4 w-4 mr-2" /> Send digest now</Button>
          <Button variant="outline" onClick={openPreview} data-testid="preview-digest-button"><Eye className="h-4 w-4 mr-2" /> Preview digest</Button>
        </div>
      </div>
      )}

      {isRole(ROLES.CEO) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-6 mt-6" data-testid="manage-lists-card">
          <div className="flex items-center gap-2 mb-1">
            <LayoutTemplate className="h-5 w-5 text-slate-500" />
            <div className="font-heading font-semibold text-slate-800">Manage lists</div>
          </div>
          <p className="text-sm text-slate-500 mb-5">Add, rename or remove the options that appear in dropdowns across the app.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
            <ListManager title="Professions" description="Shown when adding or editing an employee." endpoint="/professions" testPrefix="profession" />
            <ListManager title="Employee designations" description="Shown when adding or editing an employee (Administration)." endpoint="/employee-categories" testPrefix="employee-category" />
            <ListManager title="Employee categories" description="Employment type — shown when adding or editing an employee." endpoint="/staff-categories" testPrefix="staff-category-mgr" />
            <ListManager title="Project categories" description="Shown when creating a project." endpoint="/project-categories" testPrefix="category" />
            <ListManager title="Payment purposes" description="Shown when recording a payment." endpoint="/payment-purposes" testPrefix="payment-purpose" />
            <ListManager title="Check purposes" description="Shown when issuing a payment check." endpoint="/check-purposes" testPrefix="check-purpose" />
            <ListManager title="Income sources" description="Shown when recording income." endpoint="/income-sources" testPrefix="income-source" />
            <ListManager title="Expenditure purposes" description="Shown when recording an expenditure." endpoint="/expenditure-purposes" testPrefix="expenditure-purpose" />
            <ListManager title="Request purposes" description="Shown when creating a financial request." endpoint="/request-purposes" testPrefix="request-purpose" />
            <ListManager title="Request items" description="Selectable line items on a financial request." endpoint="/request-items" testPrefix="request-item" />
          </div>
        </div>
      )}

      {isRole(ROLES.CEO) && <AnnouncementsManager />}

      <ChangePasswordCard />
      {isRole(ROLES.CEO) && <LoginActivityCard />}
      {isRole(ROLES.CEO) && <BackupsCard />}

      {isRole(ROLES.CEO) && company && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5 mt-6" data-testid="branding-card">
          <div className="flex items-center gap-2">
            <Palette className="h-5 w-5 text-slate-500" />
            <div>
              <div className="font-heading font-semibold text-slate-800">Client portal branding</div>
              <div className="text-sm text-slate-500">Logo &amp; accent colour for <b>{company.name}</b>'s client portals.</div>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden border border-slate-200" data-testid="portal-header-preview">
            <div className={`px-5 py-4 text-white flex items-center gap-3 ${(company.logo_position || "left") === "center" ? "flex-col text-center" : "justify-between"}`}
              style={{ backgroundColor: company.accent_color || "#0B132B" }}>
              <div className={`flex items-center gap-3 ${(company.logo_position || "left") === "center" ? "flex-col" : ""}`}>
                <img src={company.logo_url || "https://static.prod-images.emergentagent.com/jobs/ec99e417-b4fc-4ffa-8f63-7f2d00809cac/images/bd73cb75e48b429917052652638a8324a647ba227ffe71085c2ef15956d66db8.jpeg"} alt="logo" className="h-10 w-10 rounded-lg object-cover bg-white/10" />
                <div className={(company.logo_position || "left") === "center" ? "text-center" : ""}>
                  <div className="font-heading font-bold" data-testid="preview-name">{company.name}</div>
                  <div className={`text-[11px] tracking-wide text-sky-300 ${company.tagline ? "" : "uppercase"}`} data-testid="preview-tagline">{company.tagline || "Client Project Portal"}</div>
                </div>
              </div>
              {(company.logo_position || "left") !== "center" && <span className="text-[11px] text-slate-300">Read-only view</span>}
            </div>
            <div className="bg-slate-50 p-4 space-y-3" data-testid="portal-body-preview">
              <div className="grid grid-cols-3 gap-2">
                {[["Income", "4,500,000"], ["Spent", "2,100,000"], ["Balance", "2,400,000"]].map(([k, v]) => (
                  <div key={k} className="bg-white rounded-lg border border-slate-100 px-2 py-2 text-center">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">{k}</div>
                    <div className="text-xs font-semibold text-slate-800 font-mono-num">{v}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1"><span>Progress</span><span className="font-semibold" style={{ color: company.accent_color || "#0B132B" }}>50%</span></div>
                <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full" style={{ width: "50%", backgroundColor: company.accent_color || "#0B132B" }} /></div>
              </div>
              <ol className="space-y-1.5">
                {[["Planning", "#10b981"], ["Installation", company.accent_color || "#0B132B"], ["Handover", "#cbd5e1"]].map(([st, col]) => (
                  <li key={st} className="flex items-center gap-2 text-xs text-slate-600"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: col }} />{st}</li>
                ))}
              </ol>
              <div className="text-[10px] text-slate-400 italic">Sample preview — the live portal shows each project's real data.</div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-5 space-y-2">
            <Label className="text-sm">Logo image</Label>
            <div className="flex items-center gap-2">
              <Input data-testid="brand-logo-input" value={company.logo_url} placeholder="https://…/logo.png"
                onChange={(e) => setCompany({ ...company, logo_url: e.target.value })} className="flex-1" />
              <input id="logo-file" type="file" accept="image/*" className="hidden" data-testid="brand-logo-file" onChange={(e) => uploadLogo(e.target.files?.[0])} />
              <Button variant="outline" data-testid="brand-logo-upload" onClick={() => document.getElementById("logo-file").click()}><Upload className="h-4 w-4 mr-2" /> Upload</Button>
            </div>
            <p className="text-xs text-slate-400">Paste an image URL or upload a PNG/JPG/WEBP file (max 10 MB).</p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Logo position in header</Label>
            <div className="flex gap-2" data-testid="logo-position">
              {["left", "center"].map((pos) => (
                <button key={pos} type="button" data-testid={`logo-pos-${pos}`} onClick={() => setCompany({ ...company, logo_position: pos })}
                  className={`px-4 py-1.5 rounded-lg text-sm border capitalize ${(company.logo_position || "left") === pos ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200"}`}>{pos}</button>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Portal tagline (optional)</Label>
            <Input value={company.tagline || ""} onChange={(e) => setCompany({ ...company, tagline: e.target.value })} placeholder="e.g. Powering Central Africa" maxLength={60} data-testid="brand-tagline" />
            <p className="text-xs text-slate-400">Shown under your logo on the client portal.</p>
          </div>

          <div className="border-t border-slate-100 pt-5 space-y-4" data-testid="login-panel-editor">
            <div>
              <div className="font-heading font-semibold text-slate-800 text-sm">Login page (left panel)</div>
              <div className="text-sm text-slate-500">Edit the text and background photo shown on the sign-in screen.</div>
            </div>

            <div className="rounded-xl overflow-hidden border border-slate-200 relative h-56 flex flex-col items-center justify-center text-center p-4" data-testid="login-preview"
              style={company.login_bg_url ? { backgroundImage: `url(${company.login_bg_url})`, backgroundSize: "cover", backgroundPosition: "center" } : { background: "#0B132B" }}>
              {!company.login_bg_url && <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(300px circle at 20% 30%, #1D4ED8, transparent), radial-gradient(260px circle at 80% 70%, #0284C7, transparent)" }} />}
              {company.login_bg_url && <div className="absolute inset-0" style={{ backgroundColor: `rgba(11,19,43,${(company.login_overlay ?? 75) / 100})` }} />}
              <div className={`relative flex flex-col items-center w-full ${(company.login_logo_position || "center") === "top" ? "justify-start" : "justify-center flex-1"}`}>
                {company.logo_url
                  ? <img src={company.logo_url} alt="logo" className="rounded-xl object-contain bg-white/95 p-1 shadow" style={{ width: (company.login_logo_size ?? 80) / 1.7, height: (company.login_logo_size ?? 80) / 1.7 }} />
                  : <div className="rounded-xl bg-gradient-to-br from-sky-400 to-blue-700 flex items-center justify-center font-extrabold text-white" style={{ width: (company.login_logo_size ?? 80) / 1.7, height: (company.login_logo_size ?? 80) / 1.7 }}>ST</div>}
                <div className="mt-2 font-heading font-extrabold text-white text-lg leading-tight">{company.name}</div>
                <div className="text-[10px] text-sky-300 uppercase tracking-wider mt-0.5">{company.login_heading || "Management System · STEMS"}</div>
                <div className="text-[11px] text-slate-100/90 mt-1 max-w-[240px]">{company.login_tagline || company.tagline || "Run your entire enterprise from one command center."}</div>
              </div>
              <div className="absolute bottom-2 text-[9px] text-slate-300/80">{company.login_footer || "@SilasTech Enterprise. Cameroon"}</div>
            </div>
            <p className="text-xs text-slate-400 -mt-2">Live preview — updates as you edit below. Click <b>Save Branding</b> to publish.</p>
            <div className="space-y-2">
              <Label className="text-sm">Heading line</Label>
              <Input value={company.login_heading || ""} onChange={(e) => setCompany({ ...company, login_heading: e.target.value })} placeholder="Management System · STEMS" maxLength={60} data-testid="login-heading" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Tagline sentence</Label>
              <Input value={company.login_tagline || ""} onChange={(e) => setCompany({ ...company, login_tagline: e.target.value })} placeholder="Run your entire enterprise from one command center." maxLength={140} data-testid="login-tagline" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Background photo</Label>
              {company.login_bg_url ? (
                <div className="relative rounded-lg overflow-hidden border border-slate-200" data-testid="login-bg-preview">
                  <img src={company.login_bg_url} alt="Login background" className="w-full h-36 object-cover" />
                  <div className="absolute inset-0 bg-[#0B132B]/75 flex items-center justify-center">
                    <span className="text-white text-sm font-medium">Preview · dark overlay applied</span>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 h-36 flex items-center justify-center text-sm text-slate-400">No photo — a navy gradient is used.</div>
              )}
              <input id="loginbg-file" type="file" accept="image/*" className="hidden" data-testid="login-bg-file" onChange={(e) => uploadLoginBg(e.target.files?.[0])} />
              <div className="flex items-center gap-2">
                <Button variant="outline" data-testid="login-bg-upload" onClick={() => document.getElementById("loginbg-file").click()}><Upload className="h-4 w-4 mr-2" /> {company.login_bg_url ? "Replace photo" : "Upload photo"}</Button>
                {company.login_bg_url && <Button variant="ghost" data-testid="login-bg-remove" onClick={() => setCompany({ ...company, login_bg_url: "" })}>Remove</Button>}
              </div>
              <p className="text-xs text-slate-400">A dark overlay is applied automatically so the logo and text stay readable. Click <b>Save Branding</b> to apply. Max 10 MB.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Preset backgrounds</Label>
              <div className="grid grid-cols-4 gap-2" data-testid="login-bg-presets">
                {[...LOGIN_PRESETS, ...(company.login_bg_presets || [])].map((p) => {
                  const isCustom = (company.login_bg_presets || []).some((x) => x.id === p.id);
                  return (
                    <div key={p.id || p.url} className="relative">
                      <button type="button" data-testid={`login-preset-${p.id}`} title={p.label}
                        onClick={() => setCompany({ ...company, login_bg_url: p.url })}
                        className={`relative rounded-lg overflow-hidden border h-16 w-full transition-all hover:opacity-90 ${company.login_bg_url === p.url ? "ring-2 ring-[#1E3A8A] border-[#1E3A8A]" : "border-slate-200"}`}>
                        <img src={p.url} alt={p.label} className="w-full h-full object-cover" />
                      </button>
                      {isCustom && (
                        <button type="button" data-testid={`login-preset-delete-${p.id}`} title="Remove preset"
                          onClick={() => deleteLoginPreset(p.id)}
                          className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow hover:bg-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
                <input id="preset-file" type="file" accept="image/*" className="hidden" data-testid="login-preset-file" onChange={(e) => { uploadLoginPreset(e.target.files?.[0]); e.target.value = ""; }} />
                <button type="button" data-testid="login-preset-add" onClick={() => document.getElementById("preset-file").click()}
                  className="rounded-lg border border-dashed border-slate-300 bg-slate-50 h-16 flex flex-col items-center justify-center text-slate-400 hover:border-[#1E3A8A] hover:text-[#1E3A8A] transition-colors">
                  <Plus className="h-4 w-4" />
                  <span className="text-[10px] mt-0.5">Add photo</span>
                </button>
              </div>
              <p className="text-xs text-slate-400">Click a preset to use it, upload your own to build a gallery, or use the photo uploader above — then Save Branding.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Photo overlay darkness</Label>
                <span className="text-xs text-slate-500" data-testid="login-overlay-value">{company.login_overlay ?? 75}%</span>
              </div>
              <Slider value={[company.login_overlay ?? 75]} min={0} max={90} step={5}
                onValueChange={([v]) => setCompany({ ...company, login_overlay: v })} data-testid="login-overlay" />
              <p className="text-xs text-slate-400">Higher = darker overlay for better text contrast. Applies to the login background photo.</p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Logo size</Label>
                <span className="text-xs text-slate-500" data-testid="login-logo-size-value">{company.login_logo_size ?? 80}px</span>
              </div>
              <Slider value={[company.login_logo_size ?? 80]} min={48} max={140} step={4}
                onValueChange={([v]) => setCompany({ ...company, login_logo_size: v })} data-testid="login-logo-size" />
              <p className="text-xs text-slate-400">Make the login logo larger or smaller to suit your background photo.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Footer line</Label>
              <Input value={company.login_footer ?? ""} onChange={(e) => setCompany({ ...company, login_footer: e.target.value })} placeholder="@SilasTech Enterprise. Cameroon" maxLength={80} data-testid="login-footer" />
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Logo &amp; text position</Label>
              <div className="flex gap-2" data-testid="login-logo-position">
                {["center", "top"].map((pos) => (
                  <button key={pos} type="button" data-testid={`login-pos-${pos}`} onClick={() => setCompany({ ...company, login_logo_position: pos })}
                    className={`px-4 py-1.5 rounded-lg text-sm border capitalize ${(company.login_logo_position || "center") === pos ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200"}`}>{pos}</button>
                ))}
              </div>
            </div>

            <div className="pt-1">
              <Button variant="outline" data-testid="login-reset-btn" onClick={resetLoginPanel} className="text-slate-600">
                <RotateCcw className="h-4 w-4 mr-2" /> Reset login panel to defaults
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Accent colour</Label>
            {logoColors.length > 0 && (
              <div className="mb-2" data-testid="logo-colors">
                <div className="text-xs text-slate-400 mb-1">From your logo · drag to reorder (first is applied)</div>
                <div className="flex gap-2">
                  {logoColors.map((c, i) => (
                    <button key={c} type="button" title={c} data-testid={`logo-color-${c.replace('#', '')}`}
                      draggable onDragStart={() => setColorDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => reorderColor(i)} onDragEnd={() => setColorDrag(null)}
                      onClick={() => setCompany((cp) => ({ ...cp, accent_color: c }))}
                      className={`h-8 w-8 rounded-full border-2 transition cursor-grab active:cursor-grabbing ${company.accent_color?.toLowerCase() === c.toLowerCase() ? "border-slate-900 scale-110" : "border-white shadow"} ${colorDrag === i ? "opacity-40" : ""}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2 mb-2" data-testid="accent-palettes">
              {PALETTES.map((pl) => (
                <button key={pl.color} type="button" title={pl.name} data-testid={`accent-${pl.color.replace('#','')}`}
                  onClick={() => setCompany({ ...company, accent_color: pl.color })}
                  className={`h-8 w-8 rounded-full border-2 transition ${company.accent_color?.toLowerCase() === pl.color.toLowerCase() ? "border-slate-900 scale-110" : "border-white shadow"}`}
                  style={{ backgroundColor: pl.color }} />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <input type="color" data-testid="brand-color-input" value={company.accent_color}
                onChange={(e) => setCompany({ ...company, accent_color: e.target.value })}
                className="h-10 w-14 rounded border border-slate-200 cursor-pointer" />
              <Input value={company.accent_color} onChange={(e) => setCompany({ ...company, accent_color: e.target.value })} className="w-40 font-mono-num" />
              <div className="flex-1 flex items-center gap-3 justify-end">
                <img src={company.logo_url || "https://static.prod-images.emergentagent.com/jobs/ec99e417-b4fc-4ffa-8f63-7f2d00809cac/images/bd73cb75e48b429917052652638a8324a647ba227ffe71085c2ef15956d66db8.jpeg"} alt="preview" className="h-9 w-9 rounded-lg object-cover" />
                <span className="text-xs px-3 py-2 rounded-lg text-white font-semibold" style={{ backgroundColor: company.accent_color }} data-testid="brand-preview">Portal header</span>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 pt-5">
            <Button onClick={saveBrand} data-testid="save-branding-button" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Save className="h-4 w-4 mr-2" /> Save Branding</Button>
          </div>
        </div>
      )}

      {isRole(ROLES.CEO) && (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-3 mt-6" data-testid="templates-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LayoutTemplate className="h-5 w-5 text-slate-500" />
            <div>
              <div className="font-heading font-semibold text-slate-800">Milestone templates</div>
              <div className="text-sm text-slate-500">Reusable timeline presets for new projects{company ? ` in ${company.name}` : ""}.</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setTplOpen(true)} data-testid="new-template-button"><Plus className="h-4 w-4 mr-1" /> New template</Button>
        </div>
        {templates.length === 0 ? (
          <p className="text-sm text-slate-400 border-t border-slate-100 pt-3">No templates yet. Create one here or save one from a project's timeline editor.</p>
        ) : templates.map((t) => (
          <div key={t.id} className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3" data-testid={`tpl-row-${t.id}`}>
            <Input value={t.name} onChange={(e) => renameTpl(t.id, e.target.value)} className="flex-1 min-w-[160px]" data-testid={`tpl-name-${t.id}`} />
            <Select value={t.category || "none"} onValueChange={(v) => patchTpl(t.id, { category: v === "none" ? "" : v })}>
              <SelectTrigger className="w-44" data-testid={`tpl-cat-${t.id}`}><SelectValue placeholder="Any type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Any type</SelectItem>
                {(meta.project_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400 w-16 shrink-0">{t.stages.length} stages</span>
            <Button variant="outline" size="sm" onClick={() => saveTpl(t)} data-testid={`tpl-save-${t.id}`}>Save</Button>
            <Button variant="ghost" size="icon" className="text-slate-500" onClick={() => duplicateTpl(t)} data-testid={`tpl-duplicate-${t.id}`}><Copy className="h-4 w-4" /></Button>
            {allCompanies.filter((c) => c.id !== t.company_id).length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-slate-500" data-testid={`tpl-copyto-${t.id}`} title="Copy to another unit"><Share2 className="h-4 w-4" /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {allCompanies.filter((c) => c.id !== t.company_id).map((c) => (
                    <DropdownMenuItem key={c.id} data-testid={`tpl-copyto-${t.id}-${c.id}`} onClick={() => copyTplTo(t, c.id, c.name)}>Copy to {c.name}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <Button variant="ghost" size="icon" className="text-red-600" onClick={() => deleteTpl(t)} data-testid={`tpl-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      )}

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Digest preview — {preview?.count || 0} alert(s) today</DialogTitle></DialogHeader>
          <iframe title="digest-preview" data-testid="digest-preview-frame" srcDoc={preview?.html || ""}
            className="w-full h-[440px] border border-slate-200 rounded-lg bg-white" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Close</Button>
            <Button onClick={sendPreviewToMe} data-testid="send-preview-to-me" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Send className="h-4 w-4 mr-2" /> Send to my inbox</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={tplOpen} onOpenChange={setTplOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New milestone template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Template name</Label>
              <Input value={newTpl.name} onChange={(e) => setNewTpl({ ...newTpl, name: e.target.value })} placeholder="e.g. Hospital Equipment Install" data-testid="new-tpl-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Project type (optional)</Label>
              <Select value={newTpl.category || "none"} onValueChange={(v) => setNewTpl({ ...newTpl, category: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="new-tpl-category"><SelectValue placeholder="Any type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Any type</SelectItem>
                  {(meta.project_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Stages</Label>
              {newTpl.stages.map((st, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-5 text-right">{i + 1}.</span>
                  <Input value={st} data-testid={`new-tpl-stage-${i}`}
                    onChange={(e) => setNewTpl((n) => ({ ...n, stages: n.stages.map((x, j) => (j === i ? e.target.value : x)) }))}
                    placeholder="Stage name" className="flex-1" />
                  <Button variant="ghost" size="icon" className="text-slate-400" data-testid={`new-tpl-stage-remove-${i}`}
                    onClick={() => setNewTpl((n) => ({ ...n, stages: n.stages.length > 1 ? n.stages.filter((_, j) => j !== i) : n.stages }))}><X className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" data-testid="new-tpl-add-stage" onClick={() => setNewTpl((n) => ({ ...n, stages: [...n.stages, ""] }))}><Plus className="h-4 w-4 mr-1" /> Add stage</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTplOpen(false)}>Cancel</Button>
            <Button onClick={createTpl} data-testid="new-tpl-create" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Create Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (nw.length < 6) return toast.error("New password must be at least 6 characters");
    if (nw !== cf) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password changed");
      setCur(""); setNw(""); setCf("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mt-6" data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-5 w-5 text-slate-500" />
        <div className="font-heading font-semibold text-slate-800">Account security</div>
      </div>
      <p className="text-sm text-slate-500 mb-5">Change your own password. You'll stay signed in on this device.</p>
      <div className="grid sm:grid-cols-3 gap-3 max-w-2xl">
        <div className="space-y-1.5"><Label className="text-sm">Current password</Label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" /></div>
        <div className="space-y-1.5"><Label className="text-sm">New password</Label>
          <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="cp-new" /></div>
        <div className="space-y-1.5"><Label className="text-sm">Confirm new</Label>
          <Input type="password" value={cf} onChange={(e) => setCf(e.target.value)} data-testid="cp-confirm" /></div>
      </div>
      <Button className="mt-4 bg-[#1E3A8A] hover:bg-[#1D4ED8]" onClick={submit} disabled={busy || !cur || !nw} data-testid="cp-save">
        <Save className="h-4 w-4 mr-2" /> Update password
      </Button>
    </div>
  );
}

function BackupsCard() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/backups").then(({ data }) => setRows(data)).catch(() => setRows([]));
    api.get("/backups/status").then(({ data }) => setStatus(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  const kb = (b) => `${(b / 1024).toFixed(1)} KB`;
  const runNow = async () => {
    setBusy(true);
    try { await api.post("/backups/run"); toast.success("Backup created"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Backup failed"); }
    finally { setBusy(false); }
  };
  const download = async (filename) => {
    try { await downloadExport(`/backups/${filename}/download`, filename); }
    catch { toast.error("Download failed"); }
  };
  const warn = status && (status.last_error || (status.last_success && status.stale));
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mt-6" data-testid="backups-card">
      <div className="flex items-center gap-2 mb-1">
        <Database className="h-5 w-5 text-slate-500" />
        <div className="font-heading font-semibold text-slate-800">Database backups</div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={runNow} disabled={busy} data-testid="backup-run-now">
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Back up now
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-4">Automatic daily snapshots are stored encrypted, safely off the app. The latest 14 are kept. Downloaded copies are encrypted — keep them together with your backup key to restore later.</p>
      {warn && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="backup-warning">
          {status.last_error
            ? `Last scheduled backup failed: ${status.last_error}`
            : `No successful backup in over 48 hours${status.last_success ? ` (last: ${fmt(status.last_success)})` : ""}. Run one now.`}
        </div>
      )}
      {rows === null ? (
        <div className="text-sm text-slate-400" data-testid="backups-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-slate-400" data-testid="backups-empty">No backups yet. Click "Back up now" to create the first one.</div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
          {rows.map((b) => (
            <div key={b.filename} className="flex items-center justify-between px-3 py-2.5" data-testid={`backup-row-${b.filename}`}>
              <div>
                <div className="text-sm font-medium text-slate-800">{fmt(b.created_at)}</div>
                <div className="text-xs text-slate-400">{kb(b.size)} · {b.documents} records · {b.collections} collections</div>
              </div>
              <Button size="sm" variant="outline" onClick={() => download(b.filename)} data-testid={`backup-download-${b.filename}`}>
                <Download className="h-3.5 w-3.5 mr-1" /> Download
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LoginActivityCard() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/login-activity").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const unlock = async (identifier) => {
    try { await api.post("/admin/unlock", { identifier }); toast.success("Account unlocked"); load(); }
    catch { toast.error("Unlock failed"); }
  };
  if (!data) return null;
  const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mt-6" data-testid="login-activity-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="h-5 w-5 text-slate-500" />
        <div className="font-heading font-semibold text-slate-800">Login activity</div>
        <Button size="sm" variant="outline" className="ml-auto" data-testid="export-login-activity"
          onClick={async () => {
            try { await downloadExport("/export/login-activity?format=csv", "stems_login_activity.csv"); toast.success("Export ready"); }
            catch { toast.error("Export failed"); }
          }}>
          <Save className="h-3.5 w-3.5 mr-1" /> Download CSV
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-5">Recent sign-ins and any accounts currently locked by failed attempts.</p>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Locked accounts</div>
          {(data.locked_accounts || []).length === 0 ? (
            <div className="text-sm text-slate-400" data-testid="no-locked">None — all clear.</div>
          ) : (
            <div className="space-y-2">
              {data.locked_accounts.map((l) => (
                <div key={l.identifier} className="flex items-center justify-between border border-red-100 bg-red-50 rounded-lg px-3 py-2" data-testid={`locked-${l.email}`}>
                  <div><div className="text-sm font-medium text-slate-800">{l.email}</div>
                    <div className="text-xs text-slate-500">{l.attempts} failed attempts</div></div>
                  <Button size="sm" variant="outline" onClick={() => unlock(l.identifier)} data-testid={`unlock-${l.email}`}>
                    <Unlock className="h-3.5 w-3.5 mr-1" /> Unlock
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Recent logins</div>
          {(data.recent_logins || []).length === 0 ? (
            <div className="text-sm text-slate-400">No logins recorded yet.</div>
          ) : (
            <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg">
              {data.recent_logins.slice(0, 10).map((r, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-slate-700">{r.user_name} <span className="text-slate-400">· {r.user_role}</span></span>
                  <span className="text-xs text-slate-400">{fmt(r.at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {(data.recent_unlocks || []).length > 0 && (
        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Recent unlocks</div>
          <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg" data-testid="recent-unlocks">
            {data.recent_unlocks.slice(0, 8).map((u, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-slate-700">{u.target} <span className="text-slate-400">· unlocked by {u.by}</span></span>
                <span className="text-xs text-slate-400">{fmt(u.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
